/**
 * Terra's landing page: a grid of widgets over the user's reports, lessons,
 * projects and tasks. The previous full-screen map is kept at
 * /dashboard/project-map.
 */
import { Skeleton } from "antd";
import { useContext, useMemo } from "react";
import WelcomeCard from "../components/Dashboard/WelcomeCard";
import LessonsAddedWidget from "../components/Dashboard/widgets/LessonsAddedWidget";
import LessonsStatusWidget from "../components/Dashboard/widgets/LessonsStatusWidget";
import MyTasksWidget from "../components/Dashboard/widgets/MyTasksWidget";
import ProjectMapWidget from "../components/Dashboard/widgets/ProjectMapWidget";
import ReportsApprovedDonutWidget from "../components/Dashboard/widgets/ReportsApprovedDonutWidget";
import ReportsApprovedTrendWidget from "../components/Dashboard/widgets/ReportsApprovedTrendWidget";
import ReportStatusWidget from "../components/Dashboard/widgets/ReportStatusWidget";
import usePermission from "../hooks/usePermission";
import { usePermissionStore } from "../stores/permissionStore";
import { useTenantStore } from "../stores/tenantStore";
import { useUserStore } from "../stores/userStore";
import {
	DashboardModule,
	DashboardTenant,
	tenantsWithModule,
	toDashboardTenant,
} from "../utils/dashboard/moduleTenants";
import { checkSuperAdminPriviledge } from "../utils/utils";
import { HomeContext } from "./Home";

/**
 * Placeholders while the access profile loads: only the cards every session
 * gets, in the restricted layout, so a well examiner never sees a placeholder
 * for a widget they won't get.
 */
const SKELETON_WIDGETS = [
	"dashboard-widget--welcome dashboard-widget--short",
	"dashboard-widget--map dashboard-widget--tall",
];

function DashboardSkeleton() {
	return (
		<div className="dashboard-page" aria-busy="true">
			<div className="dashboard-grid dashboard-grid--restricted">
				{SKELETON_WIDGETS.map((className) => (
					<div
						key={className}
						className={`dashboard-card dashboard-card--skeleton ${className}`}
					>
						<Skeleton active />
					</div>
				))}
			</div>
		</div>
	);
}

function Dashboard() {
	const { currentTenantKey, userInfo }: any = useContext(HomeContext);
	const tenantList = useTenantStore((state) => state.tenantList);
	const userRoles = useUserStore((state) => state.userRole);
	const permissionsReady = usePermissionStore((state) => state.isInitialized);
	// Well-examiner sessions only get the welcome card and the map.
	const restricted = usePermissionStore(
		(state) => !!state.accessProfile?.restricted
	);
	const viewProjects = usePermission("View.Project");
	const email: string | undefined = userInfo?.user?.email;

	// Each widget offers only the tenants where its module is available to the
	// user. As in the streams, only the master tenant looks across tenants;
	// anywhere else the module widgets show the current tenant alone.
	const tenants = useMemo(() => {
		const isSuperAdmin = checkSuperAdminPriviledge(userRoles);
		// The user's own tasks and counts span all their tenants, matching the
		// My Tasks page and the header badge.
		const all = tenantList
			.filter((tenant) => tenant?.role_key)
			.map(toDashboardTenant);
		const inMasterTenant = all.some(
			(tenant) => tenant.key === currentTenantKey && tenant.isMaster
		);
		const inView = (list: DashboardTenant[]) =>
			inMasterTenant
				? list
				: list.filter((tenant) => tenant.key === currentTenantKey);
		const withModule = (module: DashboardModule) =>
			inView(tenantsWithModule(tenantList, module, email, isSuperAdmin));
		return {
			all,
			core: withModule("core"),
			reporting: withModule("reporting"),
			lessons: withModule("lessons-learned"),
		};
	}, [tenantList, email, userRoles, currentTenantKey]);

	// Wait for the access profile so restricted sessions never see other widgets.
	if (!permissionsReady || !currentTenantKey) return <DashboardSkeleton />;

	const showReports = !restricted && tenants.reporting.length > 0;
	const showLessons = !restricted && tenants.lessons.length > 0;
	const showMap = viewProjects.hasPermission && tenants.core.length > 0;
	const showTasks = !restricted && tenants.all.length > 0;
	// My reports lives in the current tenant's Reporting page.
	const showMyReports =
		!restricted &&
		tenants.reporting.some((tenant) => tenant.key === currentTenantKey);

	// Page order is screen order: the grid fills its rows from it. Welcome and
	// My tasks come first, so users see their tasks as soon as they arrive.
	return (
		<div className="dashboard-page">
			<div
				className={`dashboard-grid${restricted ? " dashboard-grid--restricted" : ""}`}
			>
				<WelcomeCard
					currentTenantKey={currentTenantKey}
					tenantKeys={tenants.all.map((tenant) => tenant.key)}
					showMyReports={showMyReports}
					restricted={restricted}
				/>
				{showTasks && (
					<MyTasksWidget
						tenants={tenants.all}
						currentTenantKey={currentTenantKey}
					/>
				)}
				{showMap && (
					<ProjectMapWidget
						tenants={tenants.core}
						currentTenantKey={currentTenantKey}
					/>
				)}
				{showReports && (
					<ReportStatusWidget
						tenants={tenants.reporting}
						currentTenantKey={currentTenantKey}
					/>
				)}
				{showReports && (
					<ReportsApprovedDonutWidget
						tenants={tenants.reporting}
						currentTenantKey={currentTenantKey}
					/>
				)}
				{showReports && (
					<ReportsApprovedTrendWidget
						tenants={tenants.reporting}
						currentTenantKey={currentTenantKey}
					/>
				)}
				{showLessons && (
					<LessonsAddedWidget
						tenants={tenants.lessons}
						currentTenantKey={currentTenantKey}
					/>
				)}
				{showLessons && (
					<LessonsStatusWidget
						tenants={tenants.lessons}
						currentTenantKey={currentTenantKey}
					/>
				)}
			</div>
		</div>
	);
}

export default Dashboard;
